import React, { useState } from 'react';
import { CustomImage, IconLaptop, IconPrinter } from '@veneer/core';
import { DeviceImage, DeviceType } from 'src/types/IDevices';
import { Loading } from '@uma/react-commons/dist/index';
import { DeviceImageWrapper } from 'src/styles/styles';

const DefaultDeviceIcons: Record<DeviceType, React.JSX.Element> = {
  PrinterDevice: (
    <IconPrinter
      data-testid="default-printer-image"
      disabled
    />
  ),
  PCDevice: (
    <IconLaptop
      data-testid="default-pc-image"
      disabled
    />
  )
};

/**
 * Returns the default icon component based on the device type.
 * Falls back to PrinterDevice if type is unknown.
 */
const getDefaultIcon = (
  deviceType: DeviceType = 'PrinterDevice'
): React.JSX.Element =>
  DefaultDeviceIcons[deviceType] || DefaultDeviceIcons.PrinterDevice;

/**
 * Extracts image area (width * height) based on dimensions found in the URL.
 * Example: "image_300x250.png" → 300 * 250 = 75000.
 * Returns null if no valid dimensions are found.
 */
const extractImageArea = (url: string): number | null => {
  const regex = /(\d{1,5})[x-](\d{1,5})/i;
  const match = url.match(regex);

  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);

  return width > 0 && height > 0 ? width * height : null;
};

/**
 * Returns the first image in the array that contains a non-empty valid URL.
 */
const getFirstValidImage = (images: DeviceImage[]): DeviceImage | undefined => {
  return images.find(
    (image) => typeof image.url === 'string' && image.url.trim() !== ''
  );
};

export const DeviceImageWithLoading = ({
  src,
  ariaLabel,
  altText
}: {
  src: string;
  ariaLabel?: string;
  altText: string;
}) => {
  const [loading, setLoading] = useState(true);

  return (
    <DeviceImageWrapper data-component="DeviceImageWithLoading">
      {loading && (
        <div data-testid="device-image-loading">
          <Loading />
        </div>
      )}
      <CustomImage
        data-testid="device-image"
        src={src}
        aria-label={ariaLabel}
        {...{ alt: altText }} // force inclusion of alt prop
        style={loading ? { display: 'none' } : {}}
        onLoad={() => setLoading(false)}
      />
    </DeviceImageWrapper>
  );
};

/**
 * Determines the most appropriate image for a device:
 * 1. Uses the image with the largest area found in the URL (e.g., "300x250").
 * 2. If no image with size is found, uses the first image with a valid URL.
 * 3. If none are available, falls back to a default device icon.
 */
const getDeviceImage = (
  images: DeviceImage[] | null | undefined,
  deviceType: DeviceType,
  altText?: string
): React.JSX.Element => {
  const resolvedAltText = altText ?? 'Device image';

  if (!Array.isArray(images) || images.length === 0) {
    return getDefaultIcon(deviceType);
  }

  const imagesWithArea = images
    .map((image) => {
      const area = extractImageArea(image.url);
      return area ? { url: image.url, area } : null;
    })
    .filter((image): image is { url: string; area: number } => Boolean(image));

  if (imagesWithArea.length > 0) {
    const sortedImages = [...imagesWithArea].sort((a, b) => b.area - a.area);
    const bestImage = sortedImages[0];

    return (
      <DeviceImageWithLoading
        src={bestImage.url}
        aria-label={resolvedAltText}
        altText={resolvedAltText}
      />
    );
  }

  const validImage = getFirstValidImage(images);
  if (validImage) {
    return (
      <DeviceImageWithLoading
        src={validImage.url}
        aria-label={resolvedAltText}
        altText={resolvedAltText}
      />
    );
  }

  return getDefaultIcon(deviceType);
};

export default getDeviceImage;
